import React from 'react';
import {
  Loader2,
  X,
  Users as UsersIcon,
} from 'lucide-react';
import type { AdminUser } from '../../api/adminUsers';

export interface SharingDialogProps {
  adminUsersList: AdminUser[];
  selectedAdminIds: string[];
  shareToAll: boolean;
  sharingLoading: boolean;
  onSave: () => void;
  onClose: () => void;
  setSelectedAdminIds: React.Dispatch<React.SetStateAction<string[]>>;
  setShareToAll: React.Dispatch<React.SetStateAction<boolean>>;
}

const SharingDialog: React.FC<SharingDialogProps> = ({
  adminUsersList,
  selectedAdminIds,
  shareToAll,
  sharingLoading,
  onSave,
  onClose,
  setSelectedAdminIds,
  setShareToAll,
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <UsersIcon size={20} />
            사용자 공유 설정
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div className="mb-4">
          <label className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={shareToAll}
              onChange={(e) => {
                setShareToAll(e.target.checked);
                if (e.target.checked) setSelectedAdminIds([]);
              }}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-blue-800">모든 관리자에게 공개</span>
          </label>
        </div>

        {!shareToAll && (
          <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
            {adminUsersList.map((admin) => (
              <label key={admin.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedAdminIds.includes(admin.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedAdminIds([...selectedAdminIds, admin.id]);
                    } else {
                      setSelectedAdminIds(selectedAdminIds.filter((id) => id !== admin.id));
                    }
                  }}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{admin.name || admin.email}</div>
                  <div className="text-xs text-slate-500">{admin.email}</div>
                </div>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                    admin.role === 'super_admin'
                      ? 'bg-purple-100 text-purple-700'
                      : admin.role === 'admin'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {admin.role}
                </span>
              </label>
            ))}
            {adminUsersList.length === 0 && (
              <div className="p-4 text-center text-sm text-slate-400">관리자가 없습니다</div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            취소
          </button>
          <button
            onClick={onSave}
            disabled={sharingLoading || (!shareToAll && selectedAdminIds.length === 0)}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            {sharingLoading && <Loader2 size={14} className="animate-spin" />}
            저장
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharingDialog;
